import React from 'react';
import type { NavigateFunction } from 'react-router-dom';
import dayjs from 'dayjs';
import i18next from 'i18next';
import { TFunction } from 'i18next';
import type { SidebarStore } from '@hub/stores/ui/sidebar-store/sidebar-store';
import { GridColDef } from '@mui/x-data-grid';
import { formatDate } from '@monorepo/client-common';
import {
  formatCurrency,
  getClosedPositionInvalidReasonToText,
} from '@monorepo/client-common';
import { PagePath, TradeTalkStrategy } from '@monorepo/client-types';
import { Box, Tooltip } from '@monorepo/nova-ui';
import { PositionInvalidReason, TradingSystem } from '@monorepo/types';
import { CELL_ALIGN, DATE_FORMAT } from '../constants';

/**
 * Helper function to render currency with tooltip showing more precision
 */
export const renderCurrencyWithTooltip = (
  value: number | null | undefined,
  label: string,
): React.ReactNode => {
  if (value === null || value === undefined) {
    return '—';
  }

  const displayValue = formatCurrency(value, {
    currency: 'USD',
    decimalPlaces: 5,
  });
  const tooltipText = `${label}: ${formatCurrency(value, {
    currency: 'USD',
    decimalPlaces: 5,
  })}`;

  return (
    <Tooltip title={tooltipText}>
      <Box>{displayValue}</Box>
    </Tooltip>
  );
};

/**
 * Determines if UTC conversion should be used based on trading system
 * C-TRADER stores times in local timezone, so don't convert to UTC
 * MT5 stores times in UTC, so convert to UTC for display
 * BlackArrow stores times in America/Chicago, handled separately by formatBrokerDate
 */
export const shouldUseUtc = (tradingSystem?: TradingSystem): boolean => {
  return (
    tradingSystem !== TradingSystem.CTrader &&
    tradingSystem !== TradingSystem.BlackArrowFutures
  );
};

const BLACKARROW_BROKER_TIMEZONE = 'America/Chicago';

/**
 * Format a broker date for display, handling BlackArrow's Chicago timezone.
 * BlackArrow dates are stored as Chicago time in a UTC field — this re-interprets
 * them as Chicago and converts to the user's local timezone.
 * For other systems, falls back to formatDate with the given utc flag.
 */
export const formatBrokerDate = (
  date: string | Date,
  format: string,
  tradingSystem?: TradingSystem,
): string => {
  if (tradingSystem === TradingSystem.BlackArrowFutures) {
    const utcDate = dayjs.utc(date);
    const wallClock = utcDate.format('YYYY-MM-DD HH:mm:ss.SSS');
    return dayjs
      .tz(wallClock, BLACKARROW_BROKER_TIMEZONE)
      .local()
      .format(format);
  }

  const useUtc = shouldUseUtc(tradingSystem);
  return formatDate(date, { format, utc: useUtc });
};

/**
 * Helper function to get translated header name
 * Accepts a translation function to make it reactive to language changes
 */
export const getHeaderName = (
  key: string,
  t: TFunction<'dashboard'>,
  defaultValue: string,
): string => {
  return t(`tradesTable.columns.${key}`, { defaultValue });
};

/**
 * Helper function to format currency with 2 decimal places
 */
export const formatCurrencyStandard = (
  value: number | null | undefined,
): string => {
  if (value === null || value === undefined) {
    return '—';
  }
  return formatCurrency(value, {
    currency: 'USD',
    decimalPlaces: 2,
  });
};

/**
 * Helper function to check if a position is open (no closeDate)
 */
export const isOpenPosition = (closeDate: unknown): boolean => {
  return !closeDate;
};

/**
 * Default column configuration with common align properties
 */
export const getDefaultColumnConfig = (): Pick<
  GridColDef,
  'align' | 'headerAlign'
> => ({
  align: CELL_ALIGN,
  headerAlign: CELL_ALIGN,
});

/**
 * Formats invalid reasons into a readable string
 * Uses i18next for translations with position namespace
 */
export const formatInvalidReasons = (
  invalidReasons: PositionInvalidReason[],
  minTradeDuration?: number,
  t?: any,
): string => {
  if (!invalidReasons || invalidReasons.length === 0) {
    return '';
  }

  const translationFn =
    t || ((key: string, options?: any) => i18next.t(key, options || {}));

  const formattedReasons = invalidReasons.map((reason) =>
    getClosedPositionInvalidReasonToText({
      reason,
      tradeDuration: minTradeDuration,
      t: translationFn as any,
    }),
  );

  return formattedReasons.join(', ');
};

/**
 * Creates a handler function for publishing TradeTalk from trade score
 */
export const createHandlePublishClick = (
  navigate: NavigateFunction,
  sidebarStore: SidebarStore,
  t: TFunction<'dashboard'>,
  tradeId?: string | number,
  symbol?: string,
  metadata?: Record<string, unknown>,
) => {
  return (data: {
    score: number;
    metrics: { risk: number; precision: number; payoff: number };
    metadata?: Record<string, unknown>;
  }) => {
    const tradeMetadata = {
      tradeId,
      symbol,
      score: data.score,
      metrics: data.metrics,
      ...data.metadata,
      ...metadata,
    };

    const side = metadata?.side as string;
    const profitAndLoss = metadata?.profitAndLoss as number;
    const openDate = metadata?.openDate as string;
    const closeDate = metadata?.closeDate as string;

    const strategy =
      side === 'BUY' ? TradeTalkStrategy.Long : TradeTalkStrategy.Short;

    const pnlText =
      profitAndLoss !== undefined
        ? profitAndLoss > 0
          ? t('tradesTable.publish.profitable', 'profitable')
          : t('tradesTable.publish.loss', 'loss')
        : '';

    const title = symbol
      ? t(
          'tradesTable.publish.titleWithSymbol',
          '{{symbol}} {{side}} Analysis',
          {
            symbol,
            side: side || t('tradesTable.publish.trade', 'Trade'),
          },
        )
      : t('tradesTable.publish.title', 'Trade Analysis');

    const tradeScoreLabel = t('tradesTable.publish.tradeScore', 'Trade Score');
    const riskScoreLabel = t('tradesTable.publish.riskScore', 'Risk Score');
    const precisionScoreLabel = t(
      'tradesTable.publish.precisionScore',
      'Precision Score',
    );
    const payoffScoreLabel = t(
      'tradesTable.publish.payoffScore',
      'Payoff Score',
    );
    const symbolLabel = t('tradesTable.publish.symbol', 'Symbol');
    const sideLabel = t('tradesTable.publish.side', 'Side');
    const resultLabel = t('tradesTable.publish.result', 'Result');
    const openDateLabel = t('tradesTable.publish.openDate', 'Open Date');
    const closeDateLabel = t('tradesTable.publish.closeDate', 'Close Date');
    const shareAnalysisLabel = t(
      'tradesTable.publish.shareAnalysis',
      'Share your analysis and insights about this trade.',
    );

    const content = `<p>${tradeScoreLabel}: ${data.score}</p>
<p>${riskScoreLabel}: ${data.metrics.risk}% | ${precisionScoreLabel}: ${data.metrics.precision}% | ${payoffScoreLabel}: ${data.metrics.payoff}%</p>
${symbol ? `<p>${symbolLabel}: ${symbol}</p>` : ''}
${side ? `<p>${sideLabel}: ${side}</p>` : ''}
${pnlText ? `<p>${resultLabel}: ${pnlText}</p>` : ''}
${openDate ? `<p>${openDateLabel}: ${formatDate(openDate, { format: DATE_FORMAT })}</p>` : ''}
${closeDate ? `<p>${closeDateLabel}: ${formatDate(closeDate, { format: DATE_FORMAT })}</p>` : ''}
<p>${shareAnalysisLabel}</p>`;

    sidebarStore.updateSpeakYourMindDraft({
      title,
      content,
      currentStep: 0,
      isPublic: true,
      strategy,
      categories: '',
      categoryNames: '',
      tags: symbol ? symbol : '',
      chartData: undefined,
    });

    navigate(PagePath.Charts, {
      state: {
        openSpeakYourMind: true,
        tradeData: tradeMetadata,
      },
    });
  };
};
